import {
  Person,
  PersonComplete,
  PersonEvent,
  PersonMarketingDetails,
  PersonLookup,
  PersonUpdateFields,
  ImportReq,
  Matches,
  ImportResults,
  UploadStatus,
} from "../../types/person";
import {
  MarketingPersonListItem,
  CAMPAIGN_CONTEXT,
  EmailSentDetails,
} from "../../types/campaign";
import { PaginatedList, SearchAssetsRequestType } from "../../types/common";
import { SOURCES } from "../../types/unifiedMapping";
import { campaignRequest } from "./api";

export async function listPersonApi({
  pageSize,
  pageNo,
  searchKeyword,
  filterColumns,
}: SearchAssetsRequestType): Promise<PaginatedList<Person>> {
  return campaignRequest({
    url: "/person.list",
    data: {
      page_size: pageSize,
      page_number: pageNo,
      query: {
        search: {
          keyword: searchKeyword,
          fields: filterColumns,
        },
      },
    },
    shouldRetryApiCall: true,
  });
}

export async function createPersonApi(email: string): Promise<{
  success: boolean;
  marketing_person_id: string;
  person_id: string;
}> {
  return campaignRequest({
    url: "/person.create",
    data: {
      email,
    },
  });
}

export async function getPersonApi(
  person_id: string
): Promise<{ rows: PersonComplete[] }> {
  return campaignRequest({
    url: "/person.get",
    data: { person_id },
    shouldRetryApiCall: true,
  });
}

export async function listPersonActivitiesApi({
  pageSize,
  pageNo,
  personId,
  searchKeyword,
  filterColumns,
}: { personId: string } & SearchAssetsRequestType): Promise<
  PaginatedList<PersonEvent>
> {
  return campaignRequest({
    url: "/person.activities",
    data: {
      page_size: pageSize,
      page_number: pageNo,
      person_id: personId,
      query: {
        search: {
          keyword: searchKeyword,
          fields: filterColumns,
        },
      },
    },
    shouldRetryApiCall: true,
  });
}

export async function listPersonMarketingActivitiesApi(
  page_size: number,
  page_number: number,
  person_id: string,
  start_time: string,
  end_time: string
): Promise<PaginatedList<PersonMarketingDetails>> {
  return campaignRequest({
    url: "/person.campaign-metrics",
    data: { page_size, page_number, person_id, start_time, end_time },
    shouldRetryApiCall: true,
  });
}

export async function getEmailSentApi(
  person_id: string,
  correlation_id: string
): Promise<{ email: EmailSentDetails }> {
  return campaignRequest({
    url: "/person.email-sent",
    data: { person_id, correlation_id },
  });
}

export async function getUnsubscribeStatusApi(
  personId: string
): Promise<{ unsubscribed: boolean }> {
  return campaignRequest({
    url: "/person.unsubscribe.status",
    data: { person_id: personId },
    shouldRetryApiCall: true,
  });
}

export async function toggleUnsubscribeStatusApi(
  personId: string
): Promise<{ unsubscribed: boolean }> {
  return campaignRequest({
    url: "/person.unsubscribe.toggle",
    data: { person_id: personId },
  });
}

export async function personLookupApi(
  email: string,
  campaignContext: CAMPAIGN_CONTEXT
): Promise<{ rows: PersonLookup[] }> {
  return campaignRequest({
    url: "/person.lookup",
    data: {
      email,
      campaign_context: campaignContext,
    },
  });
}

export async function updatePersonApi(
  data: PersonUpdateFields
): Promise<{ status: boolean }> {
  return campaignRequest({
    url: "/person.update",
    data,
  });
}

export async function removePersonApi(
  contactId: string,
  email: string
): Promise<{ success: boolean }> {
  return campaignRequest({
    url: "/person.remove",
    data: { contact_id: contactId, email },
  });
}

export async function listProductPersonApi(
  personId: string
): Promise<{ product_users: { [key: string]: any }[] }> {
  return campaignRequest({
    url: "/person.product-user.list",
    data: { person_id: personId },
    shouldRetryApiCall: true,
  });
}

export async function listMarketingPersonApi(
  email: string,
  source: SOURCES
): Promise<{ rows: MarketingPersonListItem[] }> {
  return campaignRequest({
    url: "/marketing-person.list",
    data: { email, source },
    shouldRetryApiCall: true,
  });
}

export async function downloadContactsCsvTemplateApi(): Promise<Blob> {
  return campaignRequest({
    url: "/assets/contacts/template.download",
    data: {},
    method: "GET",
    responseType: "blob",
  });
}

export async function uploadContactsCsvFileApi(
  file: File
): Promise<{ filepath: string }> {
  const form = new FormData();
  form.append("file", file);

  return campaignRequest({
    url: "/assets/contacts/file.upload",
    data: form,
    headers: { "Content-Type": "multipart/form-data;boundary='contacts'" },
  });
}

export async function checkContactsCsvUploadStatusApi(
  filepath: string
): Promise<UploadStatus> {
  return campaignRequest({
    url: "/assets/contacts/file.status",
    data: { filepath },
  });
}

export async function getContactsCsvUploadMatchesApi(
  filepath: string
): Promise<Matches> {
  return campaignRequest({
    url: "/assets/contacts/file.match",
    data: { filepath },
  });
}

export async function importContactsFromCsvApi(
  data: ImportReq
): Promise<ImportResults> {
  return campaignRequest({
    url: "/assets/contacts/contacts.import",
    data,
  });
}

export async function cancelContactUploadFromCsvApi(
  filepath: string
): Promise<{ success: true }> {
  return campaignRequest({
    url: "/assets/contacts/file.cancel",
    data: { filepath },
  });
}

export async function getOrganisationTraitsApi(
  orgId: string
): Promise<{ organisation_details: [{ [key: string]: any }] }> {
  return campaignRequest({
    url: "/person.org-trait",
    data: {
      organisation_id: orgId,
    },
  });
}
